/**
 * Task Types
 * Type definitions for the task-based dashboard system
 */

// ============================================================================
// TASK ENUMS
// ============================================================================

export type TaskType =
  | 'onboarding'      // Onboarding setup tasks (created on signup)
  | 'diagnostic'      // Run a diagnostic assessment
  | 'syllabus_upload' // Upload a course syllabus
  | 'study_session'   // Practice/study session
  | 'study_plan'      // Review/update study plan
  | 'review_topic'    // Review a specific topic
  | 'complete_profile' // Complete profile setup
  | 'reading'         // Reading assignment (cases, textbook)
  | 'outline'         // Build or review outline
  | 'essay'           // Essay practice
  | 'flashcards'      // Flashcard review
  | 'practice'        // Practice questions/problems
  | 'other';          // Other/custom task

export type TaskStatus =
  | 'pending'      // Not started
  | 'in_progress'  // Currently being worked on
  | 'completed'    // Successfully completed
  | 'dismissed';   // User dismissed (may resurface later)

export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';

// Priority numeric values for database storage
export const TASK_PRIORITY_VALUES: Record<TaskPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  urgent: 3,
} as const;

// Reverse mapping for display
export const PRIORITY_FROM_VALUE: Record<number, TaskPriority> = {
  0: 'low',
  1: 'normal',
  2: 'high',
  3: 'urgent',
} as const;

// ============================================================================
// TASK METADATA
// ============================================================================

export interface TaskMetadata {
  /** Urgency message explaining why this task is important */
  urgency_message?: string;

  /** Related diagnostic session ID if applicable */
  diagnostic_session_id?: string;

  /** Last diagnostic score for this subject */
  last_diagnostic_score?: number;

  /** Weeks since last diagnostic */
  weeks_since_diagnostic?: number;

  /** URL to navigate to for this task */
  action_url?: string;

  /** Subject display label */
  subject_label?: string;

  /** Additional context data */
  [key: string]: unknown;
}

// ============================================================================
// USER TASK INTERFACE
// ============================================================================

export interface UserTask {
  id: string;
  user_id: string;

  // Task info
  title: string;
  description: string | null;
  task_type: TaskType;
  status: TaskStatus;
  priority: number;
  is_starred: boolean;

  // Scheduling
  due_date: string | null;
  scheduled_date: string | null;

  // Completion
  completed_at: string | null;
  completion_notes: string | null;

  // Time tracking
  estimated_minutes: number | null;
  actual_minutes: number | null;

  // Relationships
  related_subject: string | null;
  related_topic: string | null;
  study_plan_id: string | null;
  syllabus_id: string | null;

  // Source and links
  source: string | null;
  resource_links: string[] | null;

  // Metadata
  metadata: TaskMetadata | null;

  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

// ============================================================================
// TASK CREATION
// ============================================================================

export interface CreateTaskInput {
  title: string;
  description?: string;
  task_type: TaskType;
  priority?: number;
  due_date?: string;
  scheduled_date?: string;
  estimated_minutes?: number;
  related_subject?: string;
  related_topic?: string;
  study_plan_id?: string;
  syllabus_id?: string;
  source?: string;
  resource_links?: string[];
  metadata?: TaskMetadata;
}

export interface UpdateTaskInput {
  id: string;
  status?: TaskStatus;
  completion_notes?: string;
  actual_minutes?: number;
}

// ============================================================================
// TASK DISPLAY HELPERS
// ============================================================================

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  onboarding: 'Get Started',
  diagnostic: 'Diagnostic Assessment',
  syllabus_upload: 'Upload Syllabus',
  study_session: 'Study Session',
  study_plan: 'Study Plan',
  review_topic: 'Review Topic',
  complete_profile: 'Complete Profile',
  reading: 'Reading Assignment',
  outline: 'Build/Review Outline',
  essay: 'Essay Practice',
  flashcards: 'Flashcard Review',
  practice: 'Practice Questions',
  other: 'Other',
} as const;

export const TASK_TYPE_ICONS: Record<TaskType, string> = {
  onboarding: 'Rocket',
  diagnostic: 'Brain',
  syllabus_upload: 'FileUp',
  study_session: 'BookOpen',
  study_plan: 'CalendarDays',
  review_topic: 'RefreshCw',
  complete_profile: 'UserCog',
  reading: 'BookMarked',
  outline: 'FileText',
  essay: 'PenLine',
  flashcards: 'Layers',
  practice: 'Target',
  other: 'MoreHorizontal',
} as const;

export const PRIORITY_COLORS: Record<TaskPriority, { bg: string; text: string; border: string }> = {
  low: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-700',
  },
  normal: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
  },
  high: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
  },
  urgent: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
  },
} as const;

// ============================================================================
// TASK GENERATION CONFIG
// ============================================================================

/** Days after which a diagnostic should be suggested again */
export const DIAGNOSTIC_STALE_DAYS = 21; // 3 weeks

/** Days after which a dismissed task resurfaces */
export const DISMISS_RESURFACE_DAYS = 1;

/** Maximum active tasks to show */
export const MAX_ACTIVE_TASKS_DISPLAY = 5;

/** Maximum completed tasks to show */
export const MAX_COMPLETED_TASKS_DISPLAY = 3;
